import { useEffect, useMemo, useState } from "react"
import { Badge, Button, Drawer, List, Segmented, Tag, message } from "antd"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { GoInbox } from "react-icons/go"
import apiClient from "../../Utils/apiClient"
import { useHeaderContext } from "../../Utils/HeaderContext"
import StatusTag from "../StatusTag/StatusTag"
import EmptyState from "../EmptyState/EmptyState"
import "./JobsDrawer.scss"

const commandLabel = (command = "") => {
  if (command === "check-updates") return "Scan apps and Windows updates"
  if (
    command === "linux-update-all" ||
    command === "linux-update" ||
    command === "apt-upgrade-all" ||
    command === "apt-upgrade"
  ) {
    return "Linux OS updates"
  }
  if (command === "macos-update-all" || command === "macos-update") {
    return "macOS updates"
  }
  if (command === "windows-update-scan") return "Scan Windows updates"
  if (command === "windows-update-all" || command === "windows-update") {
    return "Windows updates"
  }
  if (command === "restore-point-create" || /^restore-point-create:/i.test(command)) {
    return "Save Windows restore point"
  }
  if (command === "restore-point-list") return "List Windows restore points"
  if (
    command === "restore-point-restore-last" ||
    command === "restore-point-restore:last"
  ) {
    return "Restore last working point"
  }
  if (/^restore-point-restore:/i.test(command)) return "System Restore"
  if (command === "perf-advice") return "Performance advice"
  if (command === "perf-snapshot") return "Performance snapshot"
  if (command === "disk-optimize" || command === "disk-cleanup")
    return "Optimize disk"
  if (command === "perf-tune:clean" || command === "perf-tune")
    return "Optimize disk"
  if (command === "perf-tune:pause-heavy") return "Pause heavy services"
  if (command === "wallpaper-apply") return "Wallpaper + lock screen"
  if (command === "wallpaper-remove") return "Remove wallpaper"
  if (command === "wireguard-apply") return "WireGuard VPN"
  if (/WAZUH_MANAGER=|wazuh-agent-4\.|wazuh:already-running/i.test(command)) {
    return "Wazuh agent install"
  }
  if (/validate VPN|agent-auth|wazuh-fix:ok|ossec\.conf address/i.test(command)) {
    return "Wazuh reconnect / fix"
  }
  if (/^windows-update-category:/i.test(command)) return "Windows updates (categories)"
  if (/^windows-update-ids:/i.test(command) || /^windows-update:/i.test(command)) {
    return "Windows updates (specific)"
  }
  if (command === "choco-upgrade-all") return "Upgrade all Chocolatey apps"
  if (command === "winget-upgrade-all") return "Upgrade all Winget apps"
  if (command === "defender-quick-scan") return "Quick Scan"
  if (command === "defender-full-scan") return "Full Scan"
  if (
    /\[DeviceManager\]\s*install-winget/i.test(command) ||
    /Repair-WinGetPackageManager|aka\.ms\/getwinget/i.test(command)
  ) {
    return "Install Winget"
  }
  if (/^choco-upgrade:/i.test(command)) {
    return `Upgrade ${command.replace(/^choco-upgrade:/i, "")}`
  }
  if (/^winget-upgrade:/i.test(command)) {
    return `Upgrade ${command.replace(/^winget-upgrade:/i, "")}`
  }
  if (/^choco-install:/i.test(command)) {
    return `Install ${command.replace(/^choco-install:/i, "")}`
  }
  if (/^winget-install:/i.test(command)) {
    return `Install ${command.replace(/^winget-install:/i, "")}`
  }
  if (/^custom-task:choco\s+upgrade\s+/i.test(command)) {
    const ids = command
      .replace(/^custom-task:choco\s+upgrade\s+/i, "")
      .split(/\s+/)
      .filter((token) => token && !token.startsWith("-"))
    if (ids.length > 2) return `Upgrade ${ids.length} Chocolatey apps`
    if (ids.length) return `Upgrade ${ids.join(", ")}`
  }
  if (
    /^install-agent:/i.test(command) ||
    command === "update-agent" ||
    /enrollment\/agent\//i.test(command)
  ) {
    return "Update Device Manager agent"
  }
  if (/^custom-task:(choco|winget|cmd)\s+/i.test(command)) {
    return command.replace(/^custom-task:/i, "")
  }
  if (/^app-block:/i.test(command)) {
    const names = command.replace(/^app-block:/i, "")
    return names ? `Block ${names}` : "Block apps"
  }
  if (/^app-allow:/i.test(command)) {
    const names = command.replace(/^app-allow:/i, "")
    return names ? `Allow ${names}` : "Allow apps"
  }
  if (/^url-block:/i.test(command)) {
    const names = command.replace(/^url-block:/i, "")
    return names ? `Block site ${names}` : "Block websites"
  }
  if (/^url-allow:/i.test(command)) {
    const names = command.replace(/^url-allow:/i, "")
    return names ? `Allow site ${names}` : "Allow websites"
  }
  if (command.startsWith("custom-task:")) return "Custom Task"
  if (command.startsWith("connect:")) return "Remote connect"
  if (command.startsWith("disconnect:")) return "Remote disconnect"
  return command || "task"
}

const IN_FLIGHT = new Set(["pending", "running"])

const canSeeJobs = (userInfo) => {
  const permissions = userInfo?.permissions || []
  return (
    userInfo?.role === "network-admin" ||
    permissions.includes("*") ||
    permissions.includes("clear-tasks")
  )
}

const JobsDrawer = () => {
  const [open, setOpen] = useState(false)
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [retrying, setRetrying] = useState("")
  const [forcing, setForcing] = useState("")
  const [filter, setFilter] = useState("active")
  const navigate = useNavigate()
  const userInfo = useSelector((state) => state.user.data)
  const { deviceTasks, updateDeviceTask, setTaskId } = useHeaderContext()

  const allowed = canSeeJobs(userInfo)

  const fetchTasks = async () => {
    setLoading(true)
    try {
      const res = await apiClient("devices/tasks", "GET", null, {
        params: { page: 1, pageSize: 40 },
      })
      setTasks(res?.data || [])
    } catch (err) {
      setTasks([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!allowed) return undefined
    fetchTasks()
    const timer = setInterval(fetchTasks, 20000)
    return () => clearInterval(timer)
  }, [allowed])

  useEffect(() => {
    if (open && allowed) fetchTasks()
  }, [open, allowed])

  const liveTasks = useMemo(() => {
    return Object.values(deviceTasks || {}).map((task, index) => ({
      _id: `live-${task.hostname}-${task.command}-${index}`,
      hostname: task.hostname,
      command: task.command,
      status: task.status,
      createdAt: task.updatedAt || task.createdAt,
    }))
  }, [deviceTasks])

  const merged = useMemo(() => {
    const seen = new Set()
    const rows = [...liveTasks, ...tasks].filter((task) => {
      const key = String(task._id || `${task.hostname}-${task.command}-${task.status}-${task.createdAt}`)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    return rows.sort((a, b) => {
      const aLive = IN_FLIGHT.has(String(a.status || "").toLowerCase()) ? 0 : 1
      const bLive = IN_FLIGHT.has(String(b.status || "").toLowerCase()) ? 0 : 1
      return aLive - bLive
    })
  }, [liveTasks, tasks])

  const visible = merged.filter((task) => {
    const status = String(task.status || "").toLowerCase()
    const command = String(task.command || "").toLowerCase()
    if (filter === "active") return IN_FLIGHT.has(status)
    if (filter === "failed") return status === "failed" || status === "error"
    if (filter === "reboot") return command.includes("reboot")
    return true
  })

  const activeCount = merged.filter((task) =>
    IN_FLIGHT.has(String(task.status || "").toLowerCase())
  ).length

  const forceRunJob = async (item) => {
    if (!item?._id || String(item._id).startsWith("live-")) {
      if (item.hostname) {
        setForcing(item._id)
        try {
          const res = await apiClient("devices/tasks/force-run", "POST", {
            hostname: item.hostname,
          })
          if (res?.success) message.success(res.message || "Force-run queued")
          else message.error(res?.message || "Could not force-run")
          fetchTasks()
        } catch (err) {
          message.error(err.message || "Could not force-run")
        } finally {
          setForcing("")
        }
      }
      return
    }
    setForcing(item._id)
    try {
      const res = await apiClient("devices/tasks/force-run", "POST", {
        ids: [item._id],
      })
      if (res?.success) message.success(res.message || "Force-run queued")
      else message.error(res?.message || "Could not force-run")
      fetchTasks()
    } catch (err) {
      message.error(err.message || "Could not force-run")
    } finally {
      setForcing("")
    }
  }

  const retryJob = async (item) => {
    if (!item.hostname || !item.command) return
    setRetrying(item._id)
    try {
      updateDeviceTask(item.hostname, item.command, {
        status: "pending",
        taskId: `retry-${Date.now()}`,
      })
      const agentUpdate =
        /^install-agent:/i.test(item.command) ||
        item.command === "update-agent" ||
        /enrollment\/agent\//i.test(item.command)
      const res = agentUpdate
        ? await apiClient(`devices/${item.hostname}/update-agent`, "POST")
        : await apiClient(`devices/${item.hostname}/create-task`, "POST", {
            hostname: item.hostname,
            command: item.command,
          })
      if (res?.taskId) setTaskId(res.taskId)
      fetchTasks()
    } catch (err) {
      fetchTasks()
    } finally {
      setRetrying("")
    }
  }

  if (!allowed) return null

  return (
    <>
      <button
        type="button"
        className="app-header__icon-btn"
        onClick={() => setOpen(true)}
        aria-label="Open jobs"
      >
        <Badge count={activeCount} size="small" offset={[-1, 2]}>
          <GoInbox size={18} />
        </Badge>
      </button>
      <Drawer
        title="Jobs"
        open={open}
        onClose={() => setOpen(false)}
        width={440}
        className="jobs-drawer"
        extra={
          <Button
            type="link"
            onClick={() => {
              setOpen(false)
              navigate("/clear-tasks")
            }}
          >
            View all
          </Button>
        }
      >
        <p className="jobs-drawer__lede">
          Sync, reboot, remote access, patch, and scan work currently queued or running.
        </p>
        <Segmented
          className="jobs-drawer__filters"
          value={filter}
          onChange={setFilter}
          options={[
            { label: "Active", value: "active" },
            { label: "Failed", value: "failed" },
            { label: "Reboot", value: "reboot" },
            { label: "All", value: "all" },
          ]}
        />
        <List
          loading={loading}
          dataSource={visible}
          locale={{
            emptyText: (
              <EmptyState
                title="No jobs in this view"
                description="Queued device tasks will appear here."
              />
            ),
          }}
          renderItem={(item) => (
            <List.Item
              actions={[
                IN_FLIGHT.has(String(item.status || "").toLowerCase()) ? (
                  <Button
                    key="force"
                    type="link"
                    size="small"
                    loading={forcing === item._id}
                    onClick={() => forceRunJob(item)}
                  >
                    Force run
                  </Button>
                ) : null,
                String(item.status || "").toLowerCase() === "failed" ||
                String(item.status || "").toLowerCase() === "error" ? (
                  <Button
                    key="retry"
                    type="link"
                    size="small"
                    loading={retrying === item._id}
                    onClick={() => retryJob(item)}
                  >
                    Retry
                  </Button>
                ) : null,
              ]}
            >
              <div className="jobs-drawer__item">
                <div className="jobs-drawer__item-top">
                  <strong>{item.hostname || "Device"}</strong>
                  <StatusTag status={item.status} />
                </div>
                <div className="jobs-drawer__item-meta">
                  <Tag>{commandLabel(item.command)}</Tag>
                </div>
                {item.output ? (
                  <div className="jobs-drawer__output">
                    {String(item.output)
                      .split("[DeviceManager]")
                      .slice(1)
                      .map((part) => part.trim().split(/\n/)[0].trim())
                      .filter(Boolean)
                      .slice(-1)[0] ||
                      String(item.output).replace(/\s+/g, " ").slice(0, 220)}
                  </div>
                ) : null}
              </div>
            </List.Item>
          )}
        />
      </Drawer>
    </>
  )
}

export default JobsDrawer
